import { Box, Flex } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { getBlocks } from 'redux/ModelsSlice';
import {
  setIsPageBarOpen,
  getDashboardBlockStatus,
  getEditInstrumentData,
  getFetchDashboardStatus,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  fetchDashboardMetadata,
  getAllInstruments,
  createInstrument,
  updateInstrument,
  setAllInstruments,
  fetchAllDataValues,
  getAllInstrumentOutputs,
  getEditInstrumentOutput,
  setAllInstrumentOutputs,
  getDashboard,
  handleEditCancel,
  getAllInstrumentOutputsStatus,
  setSelectedPage,
  getSelectedPage,
  getIsPageBarOpen,
  getTimeFilter,
  setTimeFilter,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';
import { getAllScenarios } from 'redux/ScenarioSlice';
import { isUiRevampEnabled } from 'utils/uiRevampFlag';

import useInstrumentDates from './hooks/useInstrumentDates';
import EmptyDashboard from './EmptyDashboard';
import Header from './header';
import Grid from './Grid';
import DashboardModal from './DashboardModal';
import ErrorFull from './ErrorFull';
import PageBar from './PageBar';
import PageModal from './PageModal';
import PageInfo from './PageInfo';
import useDashboardData from './hooks/useDashboardData';
import useDashboardTimeFilterBootstrap from './hooks/useDashboardTimeFilterBootstrap';
import DashboardSkeletonLoader from './DashboardSkeletonLoader';
import defaultDisplayTimeFilterFromModel from './defaultDashboardDisplayTimeFilter';

interface Properties {
  flags?: any;
}

const Dashboard = ({ flags }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const allInstruments: Array<any> = useSelector(getAllInstruments);
  const allInstrumentsOutputs: Array<any> = useSelector(getAllInstrumentOutputs);
  const allInstrumentOutputsStatus = useSelector(getAllInstrumentOutputsStatus);
  const editInstrumentOutput = useSelector(getEditInstrumentOutput);
  const timeFilter = useSelector(getTimeFilter);
  const model = useSelector(getBlocks);
  const dashboardBlockStatus = useSelector(getDashboardBlockStatus);
  const dashboardStatus = useSelector(getFetchDashboardStatus);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const widget = useSelector(getEditInstrumentData);
  const scenarios = useSelector(getAllScenarios);
  const editData = useSelector(getEditInstrumentData);
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  const isPageBarOpen = useSelector(getIsPageBarOpen);
  const { globalReportingPeriod } = useInstrumentDates();
  const revampShell = isUiRevampEnabled(flags);

  // DashboardRevamp is self-contained when the LD flag is on: always load blocks
  // and ensure a pack is in Redux. Header bootstrap may run first for URL sync only.
  useDashboardData({ loadBlocks: true, bootstrapPack: true });
  // Unconditional: no picker on this page sets the period any more, and the
  // hook no-ops as soon as one is set, so a user's own range still wins.
  useDashboardTimeFilterBootstrap({ enable: true });

  useEffect(() => {
    if (timeFilter?.start && timeFilter?.end) {
      return;
    }
    const fromModel = defaultDisplayTimeFilterFromModel(model?.time_properties);
    if (fromModel) {
      dispatch(setTimeFilter(fromModel));
      return;
    }
    const planFilters = localStorage.getItem('global-modal-filters');
    if (!planFilters || !model?.id) {
      return;
    }
    try {
      const data = JSON.parse(planFilters) as Record<
        string,
        { timeFilter?: { start?: string; end?: string } }
      >;
      const stored = data[model.id]?.timeFilter;
      if (stored?.start && stored?.end) {
        dispatch(setTimeFilter({ start: stored.start, end: stored.end }));
      }
    } catch {
      // ignore malformed localStorage
    }
  }, [dispatch, model?.id, model?.time_properties, timeFilter?.end, timeFilter?.start]);

  useEffect(() => {
    if (!scenarios?.length || !model) {
      return;
    }

    const isCurrentScenarioValid = scenarios.some(
      (item: any) => item.id === selectedDashboardScenario?.id,
    );
    if (isCurrentScenarioValid) {
      return;
    }

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[model.id] || {};
    const storedScenario = modalFilters?.scenario
      ? scenarios.find((item: any) => item.id === modalFilters.scenario.id)
      : null;
    const baseScenario = scenarios.find((item: any) => item.is_base);
    dispatch(selectDashboardScenario(storedScenario || baseScenario || scenarios[0]));
  }, [scenarios, model, selectedDashboardScenario, dispatch]);

  useEffect(() => {
    if (scenarios && scenarios?.length && model) {
      // Load compare scenario from localStorage
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      if (modalFilters?.compareScenario) {
        const scenario = scenarios.find(
          (item: any) => item.id === modalFilters?.compareScenario?.id,
        );
        // Only load if scenario exists, is different from selected scenario, and not already set
        if (
          scenario &&
          scenario.id !== selectedDashboardScenario?.id &&
          compareDashboardScenario?.id !== scenario.id
        ) {
          dispatch(selectCompareDashboardScenario(scenario));
        }
      } else if (compareDashboardScenario && !modalFilters?.compareScenario) {
        // Clear compare scenario if it's not in localStorage
        dispatch(selectCompareDashboardScenario(null));
      }
    }
  }, [scenarios, model, compareDashboardScenario, selectedDashboardScenario]);

  const isLoading = useMemo(
    () =>
      !model ||
      !selectedDashboardScenario ||
      dashboardBlockStatus !== STATUS.SUCCESS ||
      dashboardStatus !== STATUS.SUCCESS ||
      !dashboard?.id,
    [model, dashboardBlockStatus, dashboardStatus, selectedDashboardScenario, dashboard],
  );

  const handleCancel = () => {
    dispatch(handleEditCancel());
  };

  const handleCreate = () => {
    const compareScenarioId = compareDashboardScenario?.id || null;

    if (editData.id) {
      dispatch(
        updateInstrument({
          request_body: {
            ...editData,
            save: true,
            data_values: false,
            scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
            reporting_period: globalReportingPeriod,
            compare_scenario: compareScenarioId,
          },
        }),
      ).then((result) => {
        const foundIndex = allInstruments.findIndex(
          (instrument: any) => instrument.id === editData.id,
        );
        if (foundIndex > -1) {
          const list = JSON.parse(JSON.stringify(allInstruments));
          list[foundIndex] = result.payload;
          dispatch(setAllInstruments([...list]));
          dispatch(
            setAllInstrumentOutputs({
              ...allInstrumentsOutputs,
              [result.payload.id]: { ...editInstrumentOutput },
            }),
          );
        }
        handleCancel();
      });
    } else {
      dispatch(
        createInstrument({
          request_body: {
            ...editData,
            save: true,
            data_values: false,
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
            scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
            reporting_period: globalReportingPeriod,
            compare_scenario: compareScenarioId,
          },
        }),
      ).then((result: any) => {
        dispatch(setAllInstruments([...allInstruments, { ...result.payload }]));
        dispatch(
          setAllInstrumentOutputs({
            ...allInstrumentsOutputs,
            [result.payload.id]: { ...editInstrumentOutput, id: result.payload.id },
          }),
        );
        handleCancel();
      });
    }
  };

  const dashboardPageMetadata = (dashboardId: string) => {
    dispatch(fetchDashboardMetadata({ dashboardId }));
  };

  const fetchPageData = (pageId: any, scenarioId: any) => {
    if (!pageId || !scenarioId) {
      return;
    }
    dashboardPageMetadata(pageId);
    dispatch(
      fetchAllDataValues({
        pageId,
        scenarioId,
        start_date: timeFilter?.start || '',
        end_date: timeFilter?.end || '',
      }),
    );
  };

  const setSelectedPageAndPersist = (page: any) => {
    dispatch(setSelectedPage(page));
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    if (!model?.id) return;
    data[model.id] = {
      ...data[model.id],
      selectedPageId: page?.id,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  };

  useEffect(() => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[model?.id] || {};
    if (typeof modalFilters.isPageBarOpen === 'boolean') {
      dispatch(setIsPageBarOpen(modalFilters.isPageBarOpen));
    }
  }, [model?.id, dispatch]);

  useEffect(() => {
    if (dashboard?.pages?.length) {
      if (!selectedPage) {
        const pageWithMinPosition = dashboard.pages.reduce((minPage: any, page: any) =>
          page.position < minPage.position ? page : minPage,
        );
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        const modalFilters = data[model.id] || {};
        if (modalFilters?.selectedPageId) {
          const page = dashboard.pages.find((p: any) => p.id === modalFilters.selectedPageId);
          if (page) {
            setSelectedPageAndPersist(page);
          }
        } else {
          setSelectedPageAndPersist(pageWithMinPosition || dashboard?.pages[0]);
        }
      } else {
        const currentPage = dashboard?.pages.find((page: any) => page.id === selectedPage?.id);
        const pageWithMinPosition = dashboard?.pages.reduce((minPage: any, page: any) =>
          page.position < minPage.position ? page : minPage,
        );
        setSelectedPageAndPersist(currentPage || pageWithMinPosition || dashboard?.pages[0]);
      }
    }
  }, [dashboard?.pages]);

  // `fetchAllDataValues` leans on two things that load in parallel with it:
  // the model's blocks, which it uses to turn the indicator ids the API
  // returns into names, and the model's time properties, which decide the
  // reporting window. Firing before either arrives stored rows with no names
  // against the wrong period, and nothing refetched afterwards — so widgets
  // stayed blank until a tab switch or reload happened to win the race.
  const blocksReady = dashboardBlockStatus === STATUS.SUCCESS;
  const modelTimeProperties = model?.time_properties;

  useEffect(() => {
    if (selectedPage && selectedDashboardScenario && blocksReady && modelTimeProperties) {
      fetchPageData(selectedPage.id, selectedDashboardScenario?.id);
    }
    // The compare scenario is read from the store inside the thunk, so it has
    // to be watched here too — otherwise picking one left the widgets showing
    // the base scenario alone until some other change happened to refetch.
  }, [
    selectedPage?.id,
    selectedDashboardScenario?.id,
    compareDashboardScenario?.id,
    timeFilter,
    blocksReady,
    modelTimeProperties,
  ]);

  const refetchAllInstruments = () => {
    if (selectedPage?.id && selectedDashboardScenario?.id) {
      dashboardPageMetadata(selectedPage?.id);
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage?.id,
          scenarioId: selectedDashboardScenario?.id,
          start_date: timeFilter?.start || '',
          end_date: timeFilter?.end || '',
        }),
      );
    }
  };

  if (isLoading) {
    // DashboardRevamp is the only active dashboard implementation here (legacy
    // `dashboarding` is disabled), so this loader doesn't need to wait on the uiRevamp flag —
    // gating on it caused the old dark spinner to flash for a couple seconds before
    // LaunchDarkly's flags loaded and swapped in this skeleton.
    return <DashboardSkeletonLoader />;
  }

  return (
    // Flex column so the dashboard header takes its natural height and the
    // content area fills all remaining space — no magic-number calc() needed.
    // For uiRevamp, the parent (TabContent) already sized itself to
    // calc(100vh - main-nav-height), so filling 100% of it here is robust to
    // the secondary header's actual height instead of guessing it in pixels.
    // px={3} (12px) matches blocksv2's page inset (blockListV1.css: "Page
    // inset matches DimensionsRevampV2/index.tsx's px={3}") rather than the
    // main navigation header's own px={6} — the two are unrelated insets, and
    // the mismatch here is what left the secondary header's left/right
    // padding wider than blocksv2's. Header's mx='-3' trick below relies on
    // this exact value to extend its divider line to full width.
    <Box
      display='flex'
      flexDirection='column'
      px={3}
      mx={revampShell ? '0' : '-6'}
      pt={'0'}
      height={revampShell ? '100%' : 'calc(100vh - 215px)'}
      bg={
        revampShell
          ? 'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)'
          : undefined
      }
    >
      <Header />
      {/* mx={-3} cancels the outer px so PageBar can reach the left edge */}
      <Flex flex='1' minH='0' mx={-3} overflow='hidden'>
        {!revampShell && isPageBarOpen && <PageBar />}
        <Box flex='1' minW='0' overflowY='auto' className='scroll' px={3} pt={3} pb={3}>
          {allInstrumentOutputsStatus === STATUS.FAILED ? (
            <ErrorFull onRetry={refetchAllInstruments} />
          ) : (
            <>
              {selectedPage && <PageInfo onRefresh={refetchAllInstruments} />}

              {allInstruments?.length ? (
                <Grid allInstruments={allInstruments} />
              ) : (
                <EmptyDashboard />
              )}
            </>
          )}
        </Box>
      </Flex>

      {widget?.instrument_type && (
        <DashboardModal handleCancel={handleCancel} handleCreate={handleCreate} />
      )}

      <PageModal />
    </Box>
  );
};

export default withLDConsumer()(Dashboard);
